import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Building2, Pencil } from "lucide-react";

const CATEGORIES = [
  "Google Ads Agency",
  "TV Buyer",
  "Billboard",
  "Legal Directory",
  "Lead Gen",
  "Call Tracking",
  "Sponsorship",
  "Other",
];

const empty = {
  name: "",
  category: "Google Ads Agency",
  account_owner: "",
  monthly_retainer: 0,
  ad_spend_commitment: 0,
  contract_start: "",
  contract_end: "",
  auto_renewal_date: "",
  cancellation_deadline: "",
  deliverables: "",
  performance_expectations: "",
};

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const { user } = useAuth();
  const writable = can(user?.role, "vendor:write");

  const load = () => api.get("/vendors").then((r) => setVendors(r.data));
  useEffect(() => {
    load();
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (v) => {
    setEditingId(v.id);
    setForm({
      name: v.name || "",
      category: v.category || "Google Ads Agency",
      account_owner: v.account_owner || "",
      monthly_retainer: v.monthly_retainer || 0,
      ad_spend_commitment: v.ad_spend_commitment || 0,
      contract_start: v.contract_start?.slice(0, 10) || "",
      contract_end: v.contract_end?.slice(0, 10) || "",
      auto_renewal_date: v.auto_renewal_date?.slice(0, 10) || "",
      cancellation_deadline: v.cancellation_deadline?.slice(0, 10) || "",
      deliverables: v.deliverables || "",
      performance_expectations: v.performance_expectations || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Vendor name is required");
    const payload = {
      ...form,
      monthly_retainer: Number(form.monthly_retainer) || 0,
      ad_spend_commitment: Number(form.ad_spend_commitment) || 0,
      contract_start: form.contract_start || null,
      contract_end: form.contract_end || null,
      auto_renewal_date: form.auto_renewal_date || null,
      cancellation_deadline: form.cancellation_deadline || null,
    };
    try {
      if (editingId) {
        await api.put(`/vendors/${editingId}`, payload);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor added");
      }
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch {
      toast.error("Could not save the vendor");
    }
  };

  const remove = async (id) => {
    await api.delete(`/vendors/${id}`);
    toast.success("Vendor removed");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Accountability</p>
          <h1 className="font-display text-4xl font-light mt-1">Vendors & Contracts</h1>
        </div>
        {writable && (
          <Button className="bg-[#0B1F33] text-white rounded-none hover:bg-zinc-800" onClick={openNew} data-testid="add-vendor-button">
            <Plus className="h-4 w-4 mr-1" /> Add vendor
          </Button>
        )}
      </div>

      {vendors.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-16 text-center text-zinc-500">
          <Building2 className="h-8 w-8 mx-auto mb-3 text-zinc-400" />
          No vendors yet. Add your first agency, directory or media vendor.
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {vendors.map((v) => (
          <div key={v.id} className="border border-zinc-200 bg-white p-5 group" data-testid={`vendor-card-${v.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">{v.category}</p>
                <h3 className="font-display text-xl mt-1">{v.name}</h3>
              </div>
              {writable && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(v)}
                    className="text-zinc-300 hover:text-[#174A7E] transition-colors"
                    data-testid={`edit-vendor-${v.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => remove(v.id)}
                    className="text-zinc-300 hover:text-[#B54747] transition-colors"
                    data-testid={`delete-vendor-${v.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-zinc-500">Retainer</p>
                <p className="font-mono-data">{currency(v.monthly_retainer)}/mo</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Ad commitment</p>
                <p className="font-mono-data">{currency(v.ad_spend_commitment)}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Cancellation</p>
                <p className="font-mono-data text-xs">{v.cancellation_deadline?.slice(0, 10) || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Owner</p>
                <p className="text-xs">{v.account_owner || "—"}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingId ? "Edit vendor / contract" : "New vendor / contract"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Vendor name</Label>
              <Input value={form.name} onChange={upd("name")} data-testid="vendor-name-input" className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="vendor-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account owner</Label>
              <Input value={form.account_owner} onChange={upd("account_owner")} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Monthly retainer ($)</Label>
              <Input type="number" value={form.monthly_retainer} onChange={upd("monthly_retainer")} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Ad-spend commitment ($)</Label>
              <Input type="number" value={form.ad_spend_commitment} onChange={upd("ad_spend_commitment")} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Contract start</Label>
              <Input type="date" value={form.contract_start} onChange={upd("contract_start")} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Contract end</Label>
              <Input type="date" value={form.contract_end} onChange={upd("contract_end")} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Auto-renewal date</Label>
              <Input type="date" value={form.auto_renewal_date} onChange={upd("auto_renewal_date")} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Cancellation deadline</Label>
              <Input type="date" value={form.cancellation_deadline} onChange={upd("cancellation_deadline")} className="mt-1 rounded-none" />
            </div>
            <div className="col-span-2">
              <Label>Deliverables</Label>
              <Textarea value={form.deliverables} onChange={upd("deliverables")} className="mt-1 rounded-none" rows={2} />
            </div>
            <div className="col-span-2">
              <Label>Performance expectations</Label>
              <Textarea value={form.performance_expectations} onChange={upd("performance_expectations")} className="mt-1 rounded-none" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-[#0B1F33] text-white rounded-none" data-testid="save-vendor-button">
              {editingId ? "Save changes" : "Save vendor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
